import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UPDATE_LADDER_PROGRESS } from "../api/queries";
import { useErrorReporter } from "./use-error-reporter";
import { graphQLClient } from "../graphqlClient";
import { updateUserRecordCache } from "./useCurrentUser";

const updateLadderProgress = async (ladderProgress) => {
  const {
    ladderId,
    numberOfSessions,
    highScore,
    maxScore,
    learnositySessionId,
    learnosityReference,
    externalRegistrationId,
    overallStatus,
    completeDate,
  } = ladderProgress;

  return graphQLClient.request(UPDATE_LADDER_PROGRESS, {
    input: {
      ladderId,
      numberOfSessions,
      highScore,
      maxScore,
      learnositySessionId,
      learnosityReference,
      externalRegistrationId,
      overallStatus,
      completeDate,
    },
  });
};

export const useUpdateLadderProgress = () => {
  const { reportError } = useErrorReporter(
    "Could not update the Ladder progress",
  );
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: updateLadderProgress,
    onError: reportError,
    retry: 3,
  });

  const setLadderProgress = async (ladderProgress) => {
    try {
      // Server now handles updating all climber records automatically

      await mutation.mutateAsync(ladderProgress);
      await queryClient.invalidateQueries({ queryKey: ["myUserRecords"] });

      const targetLadderId = String(ladderProgress.ladderId);

      updateUserRecordCache(queryClient, (record) => {
        const existingProgress = record.ladderProgress || [];

        // Try to replace the old progress with the new progress if it exists
        const newLadderProgress = existingProgress.map((progress) =>
          String(progress.ladderId) === targetLadderId
            ? ladderProgress
            : progress,
        );

        // Check if it didn't exist and add a new one if not
        if (
          !newLadderProgress.find(
            (progress) =>
              String(progress.ladderId) === targetLadderId
          )
        ) {
          newLadderProgress.push(ladderProgress);
        }

        return {
          ...record,
          ladderProgress: newLadderProgress,
        };
      });
    } catch (error) {
      console.error("Error updating ladder progress:", error);
      return false;
    }
  };

  return { setLadderProgress };
};
